import { useState } from 'react';

function ProjectInfo() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center">
          <span className="text-xl mr-2">📖</span>
          <h3 className="text-sm font-semibold text-blue-900">
            关于 Integrity Audit 学术诚信审查平台
          </h3>
        </div>
        <span className="text-blue-600 text-sm">
          {expanded ? '收起 ▲' : '展开 ▼'}
        </span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4 text-sm text-blue-800">
          {/* 代码来源 */}
          <section>
            <h4 className="font-semibold mb-1">🔧 代码来源</h4>
            <p>
              本项目基于开源学术诚信审查工具构建，核心检测引擎包括：
            </p>
            <ul className="list-disc list-inside ml-2 mt-1 space-y-0.5">
              <li>
                <strong>academic-integrity-skill</strong>：图像相似度检测、数值异常筛查、统计一致性检验
              </li>
              <li>
                <strong>PaperConan</strong>：电子表格数值取证分析
              </li>
            </ul>
          </section>

          {/* 审查任务分类 */}
          <section>
            <h4 className="font-semibold mb-1">🔍 审查任务分类</h4>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div className="bg-white rounded p-2">
                <strong className="text-blue-700">完整审查</strong>
                <p className="text-xs text-gray-600 mt-0.5">全部检测项目综合运行</p>
              </div>
              <div className="bg-white rounded p-2">
                <strong className="text-blue-700">数值取证</strong>
                <p className="text-xs text-gray-600 mt-0.5">检测数据异常、重复、线性关系</p>
              </div>
              <div className="bg-white rounded p-2">
                <strong className="text-blue-700">图像完整性</strong>
                <p className="text-xs text-gray-600 mt-0.5">检测图片重复、拼接、过度处理</p>
              </div>
              <div className="bg-white rounded p-2">
                <strong className="text-blue-700">引用检查</strong>
                <p className="text-xs text-gray-600 mt-0.5">验证引用格式和自引合理性</p>
              </div>
              <div className="bg-white rounded p-2">
                <strong className="text-blue-700">Geng数值筛查</strong>
                <p className="text-xs text-gray-600 mt-0.5">深度数值模式异常检测</p>
              </div>
              <div className="bg-white rounded p-2">
                <strong className="text-blue-700">流式图检测</strong>
                <p className="text-xs text-gray-600 mt-0.5">流式细胞术图像异常检测</p>
              </div>
            </div>
          </section>

          {/* 审查结果分类 */}
          <section>
            <h4 className="font-semibold mb-1">📊 审查结果分类</h4>
            <div className="space-y-1.5">
              <div className="flex items-center">
                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full mr-2">低风险</span>
                <span>未检测到明显异常，建议正常审稿流程</span>
              </div>
              <div className="flex items-center">
                <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full mr-2">中风险</span>
                <span>发现可疑模式，建议作者核实并解释</span>
              </div>
              <div className="flex items-center">
                <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full mr-2">高风险</span>
                <span>多处异常，建议要求原始数据</span>
              </div>
              <div className="flex items-center">
                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full mr-2">严重风险</span>
                <span>强烈暗示数据操纵，建议深入调查</span>
              </div>
              <div className="flex items-center">
                <span className="px-2 py-0.5 text-xs bg-gray-800 text-white rounded-full mr-2">调查阈值</span>
                <span>系统性异常，建议启动正式调查</span>
              </div>
            </div>
          </section>

          {/* 解读方法 */}
          <section>
            <h4 className="font-semibold mb-1">📖 如何解读结果</h4>
            <ol className="list-decimal list-inside space-y-1 ml-1">
              <li>
                <strong>查看风险等级</strong>：首先关注总体风险评估
              </li>
              <li>
                <strong>审阅具体发现</strong>：每个发现包含检测器名称、严重程度、位置、证据
              </li>
              <li>
                <strong>考虑上下文</strong>：部分发现可能标注了"可能误报"的说明
              </li>
              <li>
                <strong>综合判断</strong>：单一异常可能有合理解释，多类异常叠加需警惕
              </li>
            </ol>
          </section>

          {/* 误判风险提示 */}
          <section className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <h4 className="font-semibold text-yellow-800 mb-1">⚠️ 可能的误判风险</h4>
            <ul className="list-disc list-inside space-y-1 text-yellow-700">
              <li>
                <strong>共享对照组</strong>：实验设计中使用共同对照组可能导致数值相似
              </li>
              <li>
                <strong>衍生指标</strong>：从同一原始数据计算的不同指标可能高度相关
              </li>
              <li>
                <strong>离散数据</strong>：计数数据或分级数据天然呈现特定分布模式
              </li>
              <li>
                <strong>图像格式转换</strong>：PDF转图片过程中的压缩可能导致相似性误判
              </li>
              <li>
                <strong>工具局限性</strong>：自动化检测无法替代领域专家的人工判断
              </li>
            </ul>
            <p className="mt-2 text-xs text-yellow-600 italic">
              本系统结果仅供辅助参考，最终判断需结合原始数据、实验记录和领域专业知识。
            </p>
          </section>
        </div>
      )}
    </div>
  );
}

export default ProjectInfo;
